import React, { createContext, useContext, useEffect, useState } from 'react';
import { getCurrentUser, fetchAuthSession, signOut, fetchUserAttributes } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { API_URL } from '../api';

const AuthContext = createContext({});

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

/**
 * Fetch the list of approved empowerment deityIds for the current user.
 * Returns an empty array on any error so a failed lookup never blocks the app.
 */
const fetchUserEmpowerments = async (idToken) => {
    try {
        const res = await fetch(`${API_URL}/temple/user-empowerments`, {
            headers: {
                Authorization: `Bearer ${idToken}`,
                'Content-Type': 'application/json',
            },
        });
        if (!res.ok) {
            console.warn('getUserEmpowerments returned', res.status);
            return [];
        }
        const data = await res.json();
        return data.empowerments || [];
    } catch (err) {
        console.warn('Failed to fetch user empowerments:', err);
        return [];
    }
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    const checkAuthState = async () => {
        try {
            const currentUser = await getCurrentUser();
            // Fetch user attributes and auth session in parallel
            const [userAttributes, session] = await Promise.all([
                fetchUserAttributes(),
                fetchAuthSession(),
            ]);

            const idToken = session.tokens?.idToken?.toString();

            // Fetch approved empowerments in the background — don't block auth
            const empowerments = idToken ? await fetchUserEmpowerments(idToken) : [];

            const enrichedUser = {
                ...currentUser,
                displayName: userAttributes.name || currentUser.username,
                email: userAttributes.email || currentUser.username,
                isAdmin: ['logan@buddhawisdom.org', 'brandon@buddhawisdom.org'].includes(userAttributes.email),
                idToken,
                empowerments, // array of approved deityIds
                avatarUrl: null,
            };

            // After loading empowerments, load user profile for avatarUrl
            try {
                const profileRes = await fetch(`${API_URL}/users/profile`, {
                    headers: { Authorization: `Bearer ${idToken}` },
                });
                if (profileRes.ok) {
                    const profileData = await profileRes.json();
                    enrichedUser.avatarUrl = profileData.profile?.avatarUrl || null;
                } else {
                    enrichedUser.avatarUrl = null;
                }
            } catch {
                enrichedUser.avatarUrl = null;
            }

            setUser(enrichedUser);
        } catch (error) {
            setUser(null);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSignOut = async () => {
        try {
            await signOut();
            setUser(null);
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    useEffect(() => {
        checkAuthState();

        // Listen for auth events
        const hubListener = (data) => {
            const { event } = data.payload;

            switch (event) {
                case 'signedIn':
                    checkAuthState();
                    break;
                case 'signedOut':
                    setUser(null);
                    break;
                default:
                    break;
            }
        };

        const unsubscribe = Hub.listen('auth', hubListener);

        return unsubscribe;
    }, []);

    const value = {
        user,
        isLoading,
        isAuthenticated: !!user,
        signOut: handleSignOut,
        refreshAuth: checkAuthState
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
